import * as React from 'react';
import { Pencil } from 'lucide-react';
import type { LanguageEntry } from '@clawhire/shared';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { SectionCard } from './SectionCard';
import { LanguagesEditor } from './LanguagesEditor';

export interface LanguagesCardProps {
  languages: LanguageEntry[];
  canEdit: boolean;
  suggestions?: readonly string[];
  /** Persist the list (`PATCH /candidates/:id { languages }`). */
  onSave: (languages: LanguageEntry[]) => Promise<unknown>;
}

const HINT =
  'Keep every language from the CV with its level: profiles and supplier e-mails must list all of them.';

/** Languages of a candidate: read view with an Edit → Save / Cancel flow around the LanguagesEditor. */
export function LanguagesCard({ languages, canEdit, suggestions, onSave }: LanguagesCardProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<LanguageEntry[]>(languages);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setDraft(languages);
  }, [languages, editing]);

  const save = async () => {
    const clean = draft
      .map((l) => ({ language: l.language.trim(), level: l.level || 'not stated' }))
      .filter((l) => l.language.length > 0);
    setSaving(true);
    try {
      await onSave(clean);
      setEditing(false);
      toast.success('Languages saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the languages');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Languages"
      description={HINT}
      actions={
        canEdit ? (
          editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void save()} loading={saving}>
                Save
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil aria-hidden="true" /> Edit
            </Button>
          )
        ) : null
      }
    >
      <LanguagesEditor
        value={editing ? draft : languages}
        onChange={setDraft}
        readOnly={!editing}
        suggestions={suggestions}
      />
    </SectionCard>
  );
}
